import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "../Auth/AuthLayout";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { send, SetCookie, TOKEN_NAME } from "../components";
import { encrtypth } from "../service/CoreFunctions";
import { PasswordInput } from "../components/ui/PasswordInput";
import { GoogleLogin } from '@react-oauth/google';
import { getPostLoginRoute } from "../lib/authRoutes";


const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const passwordRequirements = [
    { met: password.length >= 8, text: t("passwordMinLength") },
    { met: /[A-ZА-ЯӨҮ]/.test(password), text: t("passwordUppercase") },
    { met: /[0-9]/.test(password), text: t("passwordNumber") },
  ];

  const isPasswordValid = passwordRequirements.every(r => r.met);
  const isConfirmValid = confirmPassword.length > 0 && password === confirmPassword;
  const isEmailValid = email.trim().length > 0; // хүсвэл regex хийж болно
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false,
  });

  const showConfirmError =
    touched.confirmPassword &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;
  const showPasswordError =
    touched.password &&
    password.length > 0 &&
    !isPasswordValid;

  const canSubmit = isEmailValid && isPasswordValid && isConfirmValid && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error(t("passwordRequirementsNotMet"));
      return;
    }
    if (!isConfirmValid) {
      toast.error(t("passwordsNotMatch"));
      return;
    }

    setLoading(true);

    const res = await send("io0001", {
      email,
      password_hash: encrtypth(password),
    });
    setLoading(false);

    if (res.status === 'error') {
      toast.error(res.message);
      return;
    }
    toast.success(t("accountCreated"));
    sessionStorage.clear()
    localStorage.clear()
    checkedLogin(res);
  };

  const checkedLogin = (res) => {
    if (res?.status === 'error') {
      toast.error(res.message);
    } else {
      SetCookie(TOKEN_NAME, res.token);
      setAuth(res);
      navigate(getPostLoginRoute(res.user));
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    const res = await send('io0018', {
      credential: credentialResponse.credential
    }, setLoading);
    checkedLogin(res);
  };

  return (
    <AuthLayout title={t("register")} subtitle={t("getStarted")}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium">
            {t("email")}
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-11"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            {t("password")}
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, password: true }))}
              className={`pl-11 ${showPasswordError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              required
            />
          </div>

          {password && (
            <div className="flex flex-wrap gap-2 mt-2">
              {passwordRequirements.map((req, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full transition-colors ${req.met
                    ? "bg-success/10 text-success"
                    : "bg-muted text-muted-foreground"
                    }`}
                >
                  <Check
                    className={`h-3 w-3 ${req.met ? "opacity-100" : "opacity-40"
                      }`}
                  />
                  {req.text}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium">
            {t("confirmPassword")}
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <PasswordInput
              id="confirmPassword"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched((p) => ({ ...p, confirmPassword: true }))}
              className={`pl-11 ${showConfirmError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              required
            />
          </div>
          {showConfirmError && (
            <p className="text-xs text-destructive mt-1">
              {t("passwordsNotMatch")}
            </p>
          )}

        </div>

        <Button
          type="submit"
          variant="gradient"
          size="lg"
          className="w-full"
          disabled={!canSubmit}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("loading")}
            </>
          ) : (
            t("register")
          )}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          {t("hasAccount")}{" "}
          <Link
            to="/login"
            className="text-primary hover:text-primary/80 font-semibold"
          >
            {t("signIn")}
          </Link>
        </p>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-muted"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t('orContinueWith')}
            </span>
          </div>
        </div>

        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => {
              toast.error(t('googleLoginFailed'));
            }}
            ux_mode="popup"
            width="100%"
          />
        </div>
      </form>

      <p className="text-center text-xs text-muted-foreground mt-8">
        {t("termsAgree")}
      </p>
    </AuthLayout>
  );
};

export default Register;
